import * as THREE from '../extras/three'
import { System } from './System'
import { ControlPriorities } from '../extras/ControlPriorities'
import { isCourseCollision, courseCollisionGeometry, activateCourseCollisions } from '../extras/courseCollisions'
import { createNode } from '../extras/createNode'
import { drawCourseGraph } from '../extras/courseGraphCanvas'
import { storage } from '../storage'
import { CoursePerformance } from '../extras/coursePerformance'
import { CourseStationRenderer } from '../extras/courseStationRenderer'
import { localPlayerPosition } from '../extras/localPlayerPosition'
import { courseModelOrigin, courseStationPose, courseStationLines, courseStatusColor } from '../extras/courseLayout'

export class ClientCourseRoom extends System {
  constructor(world) {
    super(world)
    this.state = { enabled: false, active: false, busy: false, error: '', rooms: [], stations: [], nearbyStation: null, graphStationId: null, selected: null, count: 0, canSelect: false, needsLogin: true }
    this.collisionBodies = []
    this.worldButtons = []
    this.interactionTargets = []
    this.sequence = 0
    this.instances = null
    this.disposableMaterials = []
    this.disposableTextures = []
    this.template = null
    this.stationViews = []
    this.statusMaterials = new Map()
    this.config = null
    this.destroyed = false
    this.inflight = false
    this.revision = -1
    this.nextRefresh = 0
  }
  start() {
    this.perf = CoursePerformance.fromSearch(window.location.search)
    this.perf?.attach(this.world, this)
    this.interactionControl = this.world.controls.bind({ priority: ControlPriorities.ACTION })
    this.interactionControl.keyE.onPress = () => {
      if (!this.state.nearbyStation || this.state.active) return false
      this.keyStation = this.state.nearbyStation.id
      return true
    }
    this.interactionControl.keyE.onRelease = () => {
      const id = this.keyStation
      this.keyStation = null
      if (id && this.state.nearbyStation?.id === id) this.openGraph(id)
    }
    this.world.on('ready', this.onReady)
    this.world.on('disconnect', this.onDisconnect)
    this.world.on('ui', this.onUI)
    this.world.on('receptionist-chat', this.onReceptionist)
    this.world.on('xrSession', this.onXRSession)
  }
  onXRSession = () => {
    if (this.state.active) this.close(false)
    else this.closeWorldGraph()
    this.patch({ graphStationId: null })
  }
  onUI = state => { if (state.pane && this.state.active) this.close(false) }
  onReceptionist = state => { if (state.active && this.state.active) this.close(false) }
  onReady = async () => {
    if (this.state.enabled || this.destroyed) return
    try {
      const response = await fetch('/health')
      if (response.ok && (await response.json()).roomId === 'courseroom' && !this.destroyed) {
        this.patch({ enabled: true })
        await this.initialize()
      }
    } catch { /* unrelated room configuration is left alone */ }
  }
  onDisconnect = () => {
    this.stopPolling()
    this.close(false)
    this.clearStations()
    this.config = null
    this.patch({ error: 'Connection closed. Reconnect to continue.' })
  }
  patch(changes) {
    Object.assign(this.state, changes)
    this.world.emit('course-panel', { ...this.state })
  }
  async request(path, { method = 'GET', body, signal } = {}) {
    const response = await fetch(`${this.world.network.apiUrl}/course/${path}`, {
      method, signal, cache: 'no-store',
      headers: { Authorization: `Bearer ${storage.get('authToken')}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    })
    if (!response.ok) {
      const result = await response.json().catch(() => ({}))
      const error = new Error(result.error || `course_api_${response.status}`)
      error.status = response.status
      throw error
    }
    return path === 'station-model' ? response.arrayBuffer() : response.json()
  }
  update() {
    this.stationRenderer?.updateDistance(localPlayerPosition(this.world))
    if (this.stationRenderer) this.updateSharedButton()
    let nearest = null
    let distance = 3
    if (this.state.enabled && !this.state.active && !this.destroyed && !this.world.ui.state.pane && !this.world.receptionist?.nearby && !this.world.receptionist?.talking) {
      for (const target of this.interactionTargets) {
        const current = localPlayerPosition(this.world).distanceTo(target.position)
        if (current < distance) { distance = current; nearest = target.station }
      }
    }
    if (this.state.nearbyStation?.id !== nearest?.id) this.patch({ nearbyStation: nearest })
  }
  openGraph(stationId) {
    if (this.state.active || !this.state.stations.some(station => station.id === stationId)) return
    this.patch({ graphStationId: stationId, nearbyStation: null })
    if (this.world.xr.session) { this.openWorldGraph(stationId); return }
    this.open()
  }
  open() {
    if (!this.state.enabled || this.state.active || this.destroyed) return
    this.world.receptionist?.stopConversation(false)
    this.world.ui.togglePane(null)
    if (this.world.xr.session) {
      // The HTML panel is shown in the XR HUD and driven by the controller ray.
      this.closeWorldGraph()
      this.patch({ active: true, graphStationId: null })
      if (!this.config) this.initialize()
      return
    }
    this.world.controls.releaseAllButtons()
    this.world.controls.unlockPointer()
    this.control = this.world.controls.bind({ priority: ControlPriorities.CORE_UI })
    for (const key of ['keyW', 'keyA', 'keyS', 'keyD', 'keyE', 'space', 'touchA', 'touchB']) this.control[key].capture = true
    this.releaseReticle = this.world.ui.suppressReticle()
    this.control.escape.capture = true
    this.control.escape.onPress = () => { this.close(); return true }
    this.patch({ active: true })
    if (!this.config) this.initialize()
  }
  openWorldGraph(stationId) {
    this.closeWorldGraph()
    const target = this.interactionTargets.find(value => value.station.id === stationId)
    if (!target) return
    const ui = createNode('ui', { width: 720, height: 570, size: 0.002, backgroundColor: '#0b121d', padding: 12, gap: 8 })
    ui.add(createNode('uitext', { value: `Chart · ${target.station.name || `Smily ${stationId}`}`, fontSize: 24, color: '#ffffff' }))
    const canvas = document.createElement('canvas'); canvas.width = 720; canvas.height = 480
    drawCourseGraph(canvas, [], 'Loading…')
    const image = createNode('uiimage', { width: 696, height: 464 })
    ui.add(image)
    ui.add(createNode('uitext', { value: 'Close chart', color: '#ffffff', backgroundColor: '#24364b', padding: 10, textAlign: 'center', cursor: 'pointer', onPointerDown: e => e.stopPropagation(), onPointerUp: () => this.closeWorldGraph() }))
    ui.activate({ world: this.world })
    image.img = canvas; ui.redraw()
    // Fixed in the world, slightly farther away for comfortable reading.
    this.hideWorldGraph = this.world.pointer?.xrHud?.show(ui, { distance: 1.3 })
    this.worldGraph = ui
    this.graphOpen = true
    const roomId = this.state.selected
    const update = async () => {
      if (this.worldGraph !== ui) return
      if (this.inflight) { this.worldGraphTimer = setTimeout(update, 1200); return }
      this.inflight = true
      try {
        const data = await this.request(`rooms/${roomId}/stations/${stationId}/graph`)
        if (this.worldGraph === ui) drawCourseGraph(canvas, data.points, data.points.length ? `Session ${data.sessionId}` : 'No samples available')
      } catch (error) {
        if (this.worldGraph === ui) drawCourseGraph(canvas, [], error.message === 'no_active_session' ? 'No active session' : error.message === 'graph_denied' ? 'Session unavailable' : 'Data unavailable')
      } finally {
        this.inflight = false
        if (this.worldGraph === ui) { ui.redraw(); this.worldGraphTimer = setTimeout(update, 5000) }
      }
    }
    update()
  }
  closeWorldGraph() {
    clearTimeout(this.worldGraphTimer)
    if (this.worldGraph) this.world.pointer?.cancelForNode(this.worldGraph)
    this.hideWorldGraph?.()
    this.hideWorldGraph = null
    this.worldGraph?.deactivate()
    this.worldGraph = null
    this.graphOpen = false
  }
  close(restore = true) {
    this.closeWorldGraph()
    this.control?.release()
    this.control = null
    this.releaseReticle?.()
    this.releaseReticle = null
    this.world.controls.releaseAllButtons()
    this.patch({ active: false, graphStationId: null })
    if (restore && !this.destroyed && !this.world.xr.session) this.world.controls.lockPointer()
  }
  async initialize() {
    if (this.inflight || this.destroyed) return
    this.inflight = true
    const sequence = ++this.sequence
    this.controller = new AbortController()
    this.patch({ busy: true, error: '' })
    try {
      if (!this.config) {
        const config = await this.request('config', { signal: this.controller.signal })
        this.patch({ needsLogin: config.loginRequired })
        if (config.loginRequired) return
        if (!await this.world.environment.ready) throw new Error('course_environment_unavailable')
        if (!this.template) {
          const loadStarted = performance.now()
          const buffer = await this.request('station-model', { signal: this.controller.signal })
          const fetched = performance.now()
          const gltf = await this.world.loader.gltfLoader.parseAsync(buffer, '')
          if (this.perf) this.perf.modelLoad = { bytes: buffer.byteLength, fetchMs: Math.round(fetched - loadStarted), parseMs: Math.round(performance.now() - fetched) }
          if (this.destroyed || sequence !== this.sequence) {
            this.disposeTemplate(gltf.scene)
            return
          }
          this.template = gltf.scene
          const configuredMaterials = new Set()
          this.template.traverse(node => {
            if (this.perf?.mode !== 'baseline' && node.isMesh && !isCourseCollision(node) && node.name !== 'panel') {
              for (const material of [].concat(node.material)) {
                if (!configuredMaterials.has(material)) { this.world.setupMaterial(material); configuredMaterials.add(material) }
              }
            }
          })
          this.modelOrigin = courseModelOrigin(this.template)
          this.collisionGeometry = courseCollisionGeometry(this.template)
          if (gltf.animations.length) {
            this.disposeTemplate(this.template)
            this.template = null
            throw new Error('station_model_must_be_static')
          }
        }
        if (this.perf?.mode !== 'baseline' && !this.lodTemplate) {
          const response = await fetch('/course-smily-lod.glb', { signal: this.controller.signal })
          if (!response.ok) throw new Error('course_lod_unavailable')
          const lod = await this.world.loader.gltfLoader.parseAsync(await response.arrayBuffer(), '')
          if (this.destroyed || sequence !== this.sequence) { this.disposeTemplate(lod.scene); return }
          if (lod.animations.length) { this.disposeTemplate(lod.scene); throw new Error('course_lod_must_be_static') }
          this.lodTemplate = lod.scene
        }
        this.config = config
      }
      const data = await this.request('rooms', { signal: this.controller.signal })
      const selection = await this.request('selection', { signal: this.controller.signal })
      if (sequence !== this.sequence || this.destroyed) return
      this.patch({ rooms: data.rooms, localTest: this.config.localTest, canSelect: selection.canSelect })
      this.startPolling()
    } catch (error) {
      if (sequence === this.sequence && !this.destroyed) this.fail(error)
    } finally {
      if (sequence === this.sequence) { this.inflight = false; this.patch({ busy: false }) }
    }
  }
  async login(username, password) {
    if (this.inflight || this.destroyed) return
    this.patch({ busy: true, error: '' })
    this.inflight = true
    try {
      await this.request('login', { method: 'POST', body: { username, password } })
      this.config = null
      this.patch({ needsLogin: false })
    } catch (error) {
      this.patch({ needsLogin: true, error: error.message })
      return
    } finally { this.inflight = false; this.patch({ busy: false }) }
    await this.initialize()
  }
  startPolling() {
    if (this.timer) return
    this.timer = setInterval(() => this.poll(), 2000)
    // The list request just consumed the provider cooldown; first selection read is lightweight.
    this.nextRefresh = Date.now() + 1200
  }
  stopPolling() {
    clearInterval(this.timer)
    this.timer = null
    this.sequence++
    this.controller?.abort()
    this.inflight = false
  }
  async poll(force = false) {
    if (this.inflight || !this.config || this.destroyed) return
    this.inflight = true
    const sequence = ++this.sequence
    this.controller = new AbortController()
    try {
      const selection = await this.request('selection', { signal: this.controller.signal })
      if (sequence !== this.sequence || this.destroyed) return
      this.patch({ canSelect: selection.canSelect })
      const changed = this.state.selected !== selection.roomId
      const revised = this.revision !== selection.revision
      if (changed) {
        this.clearStations()
        this.patch({ selected: selection.roomId, count: 0 })
      }
      if (selection.roomId !== null && (changed || revised || force || (!this.graphOpen && Date.now() >= this.nextRefresh))) {
        const data = await this.request(`rooms/${selection.roomId}`, { signal: this.controller.signal })
        // A selection may have changed on the server while its detail response was in flight.
        const latest = await this.request('selection', { signal: this.controller.signal })
        if (latest.revision !== selection.revision || latest.roomId !== selection.roomId) return
        if (sequence !== this.sequence || this.destroyed) return
        this.renderStations(data.room.smilies)
        this.nextRefresh = Date.now() + data.refreshAfterSeconds * 1000
      }
      this.revision = selection.revision
      this.patch({ error: '' })
    } catch (error) {
      if (sequence === this.sequence && !this.destroyed) this.fail(error)
    } finally { if (sequence === this.sequence) this.inflight = false }
  }
  async select(roomId) {
    if (this.inflight || !this.state.canSelect || !this.config || this.destroyed) return
    this.inflight = true
    const sequence = ++this.sequence
    this.controller = new AbortController()
    this.clearStations()
    this.patch({ busy: true, error: '', count: 0 })
    try {
      const data = await this.request('selection', { method: 'POST', body: { roomId }, signal: this.controller.signal })
      const latest = await this.request('selection', { signal: this.controller.signal })
      if (sequence !== this.sequence || this.destroyed || latest.revision !== data.revision) return
      this.renderStations(data.room.smilies)
      this.revision = data.revision
      this.nextRefresh = Date.now() + data.refreshAfterSeconds * 1000
      this.patch({ selected: data.room.id })
    } catch (error) { if (sequence === this.sequence && !this.destroyed) this.fail(error) }
    finally { if (sequence === this.sequence) { this.inflight = false; this.patch({ busy: false }) } }
  }
  fail(error) {
    if ([401, 403].includes(error.status)) {
      this.clearStations()
      this.config = null
      this.stopPolling()
      this.patch({ busy: false, count: 0, rooms: [], selected: null, needsLogin: true })
    }
    this.patch({ error: error.message === 'Failed to fetch' ? 'Course API unreachable. Retry.' : error.message })
    this.nextRefresh = Date.now() + 5000
  }
  renderStations(stations) {
    const started = performance.now()
    const sourceCount = stations.length
    if (this.perf?.count !== null && this.perf?.count !== undefined) stations = stations.slice(0, this.perf.count)
    const baseline = this.perf?.mode === 'baseline'
    if (!baseline && this.lodTemplate) {
      if (this.stationRenderer && this.state.stations.length === stations.length && this.state.stations.every((station, index) => station.id === stations[index].id)) {
        this.stationRenderer.updateStations(stations)
        this.patch({ count: stations.length, stations, nearbyStation: this.state.nearbyStation ? stations.find(station => station.id === this.state.nearbyStation.id) || null : null })
        this.perf?.recordRefresh('instanced-incremental', performance.now() - started, sourceCount)
        return
      }
      this.clearStations()
      try {
        this.collisionBodies = activateCourseCollisions(this.world, this.collisionGeometry || [], stations.length, this.config.marker)
        this.stationRenderer = new CourseStationRenderer(this.world, this.template, this.lodTemplate, stations, this.config.marker)
        this.instances = this.stationRenderer.group
        this.interactionTargets = this.stationRenderer.targets
        this.world.stage.scene.add(this.instances)
        this.patch({ count: stations.length, stations })
        this.perf?.recordRefresh('instanced-rebuild', performance.now() - started, sourceCount)
      } catch (error) { this.clearStations(); throw error }
      return
    }
    if (!baseline && this.instances && this.stationViews.length === stations.length && this.stationViews.every((view, index) => view.station.id === stations[index].id)) {
      stations.forEach((station, index) => {
        const view = this.stationViews[index]
        for (const node of view.statusMeshes) node.material = this.statusMaterial(node.sourceMaterial, station.alarmState)
        this.updateStationPanels(view, station)
        view.station = station
        this.interactionTargets[index].station = station
      })
      this.patch({ count: stations.length, stations, nearbyStation: this.state.nearbyStation ? stations.find(station => station.id === this.state.nearbyStation.id) || null : null })
      this.perf?.recordRefresh('incremental', performance.now() - started, sourceCount)
      return
    }
    this.clearStations()
    const group = new THREE.Group()
    group.name = 'course-stations'
    this.instances = group
    try {
      this.collisionBodies = activateCourseCollisions(this.world, this.collisionGeometry || [], stations.length, this.config.marker)
      stations.forEach((station, index) => {
        const instance = this.template.clone(true)
        const panels = []
        const statusMeshes = []
        instance.traverse(node => {
          if (isCourseCollision(node)) { node.visible = false; return }
          if (!node.isMesh) return
          if (node.name === 'panel') { panels.push(node); return }
          if (baseline) {
            node.material = node.material.clone()
            this.disposableMaterials.push(node.material)
            if (node.material.name === 'course_avatar_status') node.material.color.setRGB(...courseStatusColor(station.alarmState))
            this.world.setupMaterial(node.material)
          } else if (node.material.name === 'course_avatar_status') {
            node.sourceMaterial = node.material
            node.material = this.statusMaterial(node.sourceMaterial, station.alarmState)
            statusMeshes.push(node)
          }
          node.castShadow = false
          node.receiveShadow = true
        })
        const view = { station, statusMeshes, panels: [], lines: null }
        for (const panel of panels) {
          // Keep the supplied panel geometry, UVs and complete hierarchy transformation.
          const canvas = document.createElement('canvas')
          canvas.width = 1024
          canvas.height = 584
          const texture = new THREE.CanvasTexture(canvas)
          texture.colorSpace = THREE.SRGBColorSpace
          // The supplied front screen UVs run right-to-left (confirmed by the user's screenshot).
          texture.repeat.x = -1
          texture.offset.x = 1
          // Blender panel is an X/Z plane: use its exported UVs, not an assumed node +Z.
          panel.material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, toneMapped: false })
          this.disposableMaterials.push(panel.material)
          this.disposableTextures.push(texture)
          view.panels.push({ canvas, texture })
        }
        this.updateStationPanels(view, station)
        this.stationViews.push(view)
        const pose = courseStationPose(index, this.config.marker)
        const pivot = new THREE.Group()
        pivot.position.fromArray(pose.position)
        pivot.quaternion.fromArray(pose.quaternion)
        const content = new THREE.Group()
        content.position.fromArray(this.modelOrigin).negate()
        content.add(instance)
        pivot.add(content)
        group.add(pivot)
      })
      group.updateMatrixWorld(true)
      group.children.forEach((pivot, index) => {
        const panel = pivot.getObjectByName('panel')
        const position = new THREE.Vector3()
        if (panel) {
          panel.geometry.computeBoundingBox()
          panel.geometry.boundingBox.getCenter(position)
          panel.localToWorld(position)
        } else pivot.getWorldPosition(position)
        const quaternion = pivot.quaternion.clone().multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI))
        this.interactionTargets.push({ station: stations[index], position, quaternion })
        const buttonPosition = position.clone(); buttonPosition.y -= 0.27
        const button = createNode('ui', { width: 300, height: 58, size: 0.002, position: buttonPosition.toArray(), quaternion: quaternion.toArray(), backgroundColor: '#24364b', borderRadius: 12, justifyContent: 'center' })
        button.add(createNode('uitext', { value: 'E · Open chart', fontSize: 23, color: '#ffffff', textAlign: 'center', padding: 12, cursor: 'pointer', onPointerDown: e => e.stopPropagation(), onPointerUp: () => {
          if (localPlayerPosition(this.world).distanceTo(position) <= 3) this.openGraph(this.interactionTargets[index]?.station.id)
        } }))
        button.activate({ world: this.world })
        this.worldButtons.push(button)
      })
      this.world.stage.scene.add(group)
      this.patch({ count: stations.length, stations })
      this.perf?.recordRefresh('rebuild', performance.now() - started, sourceCount)
    } catch (error) { this.clearStations(); throw error }
  }
  updateSharedButton() {
    if (this.sharedButtonText) {
      this.sharedButtonText.value = this.world.xr.session ? 'Trigger · Open chart' : 'E · Open chart'
    }
    let target = null
    let distance = 9
    if (!this.state.active && !this.graphOpen && !this.world.ui.state.pane && !this.world.receptionist?.talking) {
      for (const candidate of this.interactionTargets) {
        const current = localPlayerPosition(this.world).distanceToSquared(candidate.position)
        if (current < distance) { distance = current; target = candidate }
      }
    }
    if (target === this.buttonTarget) return
    if (this.sharedButton) this.world.pointer?.cancelForNode(this.sharedButton)
    this.sharedButton?.deactivate()
    this.buttonTarget = target
    this.worldButtons = []
    if (!target) return
    if (!this.sharedButton) {
      this.sharedButton = createNode('ui', { width: 300, height: 58, res: 1, size: 0.002, backgroundColor: '#24364b', borderRadius: 12, justifyContent: 'center' })
      this.sharedButtonText = createNode('uitext', { value: this.world.xr.session ? 'Trigger · Open chart' : 'E · Open chart', fontSize: 23, color: '#ffffff', textAlign: 'center', padding: 12, cursor: 'pointer', onPointerDown: e => e.stopPropagation(), onPointerUp: () => {
        const selected = this.buttonTarget
        if (selected && localPlayerPosition(this.world).distanceToSquared(selected.position) <= 9) this.openGraph(selected.station.id)
      } })
      this.sharedButton.add(this.sharedButtonText)
    }
    this.sharedButton.position.copy(target.position)
    this.sharedButton.position.y -= 0.27
    this.sharedButton.quaternion.copy(target.quaternion)
    this.sharedButton.activate({ world: this.world })
    this.worldButtons = [this.sharedButton]
  }
  statusMaterial(source, alarmState) {
    const state = alarmState === 1 ? 1 : alarmState === 2 ? 2 : 0
    let variants = this.statusMaterials.get(source)
    if (!variants) { variants = new Map(); this.statusMaterials.set(source, variants) }
    if (!variants.has(state)) {
      const material = source.clone()
      material.color.setRGB(...courseStatusColor(state))
      this.world.setupMaterial(material)
      variants.set(state, material)
    }
    return variants.get(state)
  }
  updateStationPanels(view, station) {
    const lines = courseStationLines(station)
    if (view.lines && lines.length === view.lines.length && lines.every((line, index) => line === view.lines[index])) return
    const started = performance.now()
    for (const { canvas, texture } of view.panels) {
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#111827'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.fillStyle = '#ffffff'
      ctx.font = '32px sans-serif'
      lines.forEach((line, row) => ctx.fillText(line.slice(0, 100), 24, 58 + row * 74, 976))
      texture.needsUpdate = true
    }
    view.lines = lines
    this.perf?.record('panelCpuMs', performance.now() - started)
  }
  clearStations() {
    this.stationRenderer?.dispose()
    this.stationRenderer = null
    if (this.sharedButton) this.world.pointer?.cancelForNode(this.sharedButton)
    this.sharedButton?.deactivate()
    this.sharedButton = null
    this.sharedButtonText = null
    this.buttonTarget = null
    for (const body of this.collisionBodies) body.deactivate()
    this.collisionBodies = []
    this.closeWorldGraph()
    for (const button of this.worldButtons) button.deactivate()
    this.worldButtons = []
    this.interactionTargets = []
    this.stationViews = []
    this.keyStation = null
    this.patch({ stations: [], nearbyStation: null })
    this.instances?.removeFromParent()
    this.instances = null
    for (const material of this.disposableMaterials) material.dispose()
    for (const texture of this.disposableTextures) texture.dispose()
    this.disposableMaterials = []
    this.disposableTextures = []
  }
  disposeTemplate(template) {
    const resources = new Set()
    template?.traverse(node => {
      if (!node.isMesh) return
      resources.add(node.geometry)
      for (const material of [].concat(node.material)) {
        resources.add(material)
        for (const value of Object.values(material)) if (value?.isTexture) resources.add(value)
      }
    })
    for (const resource of resources) resource.dispose()
  }
  destroy() {
    this.interactionControl?.release()
    this.interactionControl = null
    this.destroyed = true
    this.stopPolling()
    this.close(false)
    this.clearStations()
    this.perf?.destroy()
    for (const variants of this.statusMaterials.values()) for (const material of variants.values()) material.dispose()
    this.statusMaterials.clear()
    this.disposeTemplate(this.template)
    this.disposeTemplate(this.lodTemplate)
    this.lodTemplate = null
    for (const geometry of this.collisionGeometry || []) geometry.dispose()
    this.collisionGeometry = null
    this.template = null
    this.world.off('ready', this.onReady)
    this.world.off('disconnect', this.onDisconnect)
    this.world.off('ui', this.onUI)
    this.world.off('receptionist-chat', this.onReceptionist)
    this.world.off('xrSession', this.onXRSession)
  }
}
